// X3DH initial key agreement (Signal's Extended Triple Diffie-Hellman). Produces a
// shared secret SK that seeds the Double Ratchet, plus the associated data that binds
// both identities into every subsequent message.
import { concatBytes, utf8 } from "@shared/encoding.js";
import { CTX, KEY_BYTES } from "@shared/constants.js";
import { type KeyPair, dh, hash, verify } from "./sodium.js";
import type { Identity } from "./account.js";

export interface PeerBundle {
  ik_dh_pub: Uint8Array;
  ik_sig_pub: Uint8Array;
  spk_id: number;
  spk_pub: Uint8Array;
  spk_sig: Uint8Array;
  opk_id: number | null;
  opk_pub: Uint8Array | null;
}

/** X3DH init material the initiator puts in the first message header. */
export interface X3dhInitBytes {
  ik_dh: Uint8Array;
  ik_sig: Uint8Array;
  ek: Uint8Array;
  spk_id: number;
  opk_id: number | null;
}

export interface InitiatorResult {
  sk: Uint8Array;
  ad: Uint8Array;
  init: X3dhInitBytes;
  remoteRatchetPub: Uint8Array; // peer's signed prekey = initiator's first dhr
}

function deriveSK(parts: Uint8Array[]): Uint8Array {
  return hash(concatBytes(utf8(CTX.x3dhRoot), ...parts), KEY_BYTES);
}

/** Initiator (Alice). `ek` is supplied for deterministic tests; otherwise generated. */
export function x3dhInitiate(me: Identity, peer: PeerBundle, ek: KeyPair): InitiatorResult {
  if (!verify(peer.spk_sig, peer.spk_pub, peer.ik_sig_pub)) {
    throw new Error("signed prekey signature is invalid - refusing to start session");
  }
  const dh1 = dh(me.ik_dh.priv, peer.spk_pub);
  const dh2 = dh(ek.priv, peer.ik_dh_pub);
  const dh3 = dh(ek.priv, peer.spk_pub);
  const parts = [dh1, dh2, dh3];
  if (peer.opk_pub) parts.push(dh(ek.priv, peer.opk_pub));

  const sk = deriveSK(parts);
  const ad = concatBytes(me.ik_sig.pub, peer.ik_sig_pub); // A ‖ B
  return {
    sk,
    ad,
    init: { ik_dh: me.ik_dh.pub, ik_sig: me.ik_sig.pub, ek: ek.pub, spk_id: peer.spk_id, opk_id: peer.opk_id },
    remoteRatchetPub: peer.spk_pub,
  };
}

export interface ResponderResult {
  sk: Uint8Array;
  ad: Uint8Array;
}

/** Responder (Bob). `spk`/`opk` are Bob's prekey keypairs matching the ids in `init`. */
export function x3dhRespond(me: Identity, spk: KeyPair, opk: KeyPair | null, init: X3dhInitBytes): ResponderResult {
  const dh1 = dh(spk.priv, init.ik_dh);
  const dh2 = dh(me.ik_dh.priv, init.ek);
  const dh3 = dh(spk.priv, init.ek);
  const parts = [dh1, dh2, dh3];
  if (opk) parts.push(dh(opk.priv, init.ek));

  const sk = deriveSK(parts);
  const ad = concatBytes(init.ik_sig, me.ik_sig.pub); // A ‖ B (same order as initiator)
  return { sk, ad };
}
